import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";

import { Checkbox, Dropdown, Form, SubmitButton, Textbox, useForm } from "capitalrx-components-2.0/lib/forms";
import { Message, Messages } from "capitalrx-components-2.0/lib/general";
import { Stack } from "capitalrx-components-2.0/lib/layout";
import { Title } from "capitalrx-components-2.0/lib/typography";

import { findBestPriceGeolocateAPI } from "@/api";
import { FindBestPrice } from "@/api/interfaces";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { getThemeButton } from "@/utils";
import {
  CheckCoverageSearch,
  DAYS_SUPPLY_OPTIONS,
  dosageFormOptions,
  dosageOptions,
  geolocateRequest,
  pluralizeQuantity,
  packagingLabel,
  quantityOptions,
} from "./utils";

import styles from "./CheckCoverage.module.scss";

export interface CheckCoverageFormData {
  drug_id: string;
  dosage_form: string;
  dosage: string;
  quantity: string;
  use_custom_quantity: boolean;
  days_supply: string;
  use_custom_days_supply: boolean;
  location: string;
  use_current_location: boolean;
}

export function initialFormValues(drug: FindBestPrice.Drug, launchNdc?: string): CheckCoverageFormData {
  const launchConfiguration = drug.configurations.find((configuration) => !!launchNdc && configuration.ndc === launchNdc);
  const dosageForm = launchConfiguration?.dosage_form || drug.most_common_form || dosageFormOptions(drug)[0] || "";
  const dosages = dosageOptions(drug, dosageForm);
  const dosage =
    launchConfiguration?.dosage
    || dosages.find((option) => !!drug.most_common_dosage && option.includes(drug.most_common_dosage))
    || dosages[0]
    || "";
  const quantity = quantityOptions(drug, dosageForm, dosage)[0] || "";

  return {
    drug_id: String(drug.id),
    dosage_form: dosageForm,
    dosage,
    quantity,
    use_custom_quantity: !quantity,
    days_supply: DAYS_SUPPLY_OPTIONS[0],
    use_custom_days_supply: false,
    location: "",
    use_current_location: false,
  };
}

function validateCustomNumber(value: string): string | true {
  const number = Number(value);
  if (!value || isNaN(number)) return "Please enter a valid number";
  if (number < 1 || number > 9999) return "Value must be between 1 and 9999";
  return true;
}

interface CheckCoverageFormProps {
  token: string;
  drugResponse: FindBestPrice.DrugGetResponse;
  defaultValues: CheckCoverageFormData;
  onSearch: (search: CheckCoverageSearch, values: CheckCoverageFormData) => void;
}

export const CheckCoverageForm: FC<CheckCoverageFormProps> = ({ token, drugResponse, defaultValues, onSearch }) => {
  const { getAuthToken } = useContext(AuthenticationContext);
  const { primary_color } = useContext(ThemeSelectorContext);
  const form = useForm<CheckCoverageFormData>({ defaultValues });
  const { setValue } = form;

  const [currentLocation, setCurrentLocation] = useState<FindBestPrice.Location | null>(null);
  const [error, setError] = useState<string>();

  const drugId = useWatch({ control: form.control, name: "drug_id" });
  const dosageForm = useWatch({ control: form.control, name: "dosage_form" });
  const dosage = useWatch({ control: form.control, name: "dosage" });
  const useCustomQuantity = useWatch({ control: form.control, name: "use_custom_quantity" });
  const useCustomDaysSupply = useWatch({ control: form.control, name: "use_custom_days_supply" });
  const useCurrentLocation = useWatch({ control: form.control, name: "use_current_location" });

  const drugOptions = useMemo(
    () => [drugResponse.drug, ...(drugResponse.alternatives || []).map((alternative) => alternative.drug)],
    [drugResponse],
  );
  const selectedDrug = useMemo(
    () => drugOptions.find((drug) => String(drug.id) === drugId) ?? drugResponse.drug,
    [drugOptions, drugId, drugResponse],
  );
  const formOptions = useMemo(() => dosageFormOptions(selectedDrug), [selectedDrug]);
  const dosages = useMemo(() => dosageOptions(selectedDrug, dosageForm), [selectedDrug, dosageForm]);
  const quantities = useMemo(() => quantityOptions(selectedDrug, dosageForm, dosage), [selectedDrug, dosageForm, dosage]);
  const packaging = packagingLabel(selectedDrug, dosageForm);

  // each dropdown resets its dependents only when the user changes it, so prefilled values survive the first render
  const previousValues = useRef({ drugId, dosageForm, dosage, useCustomQuantity, useCustomDaysSupply });

  useEffect(() => {
    if (previousValues.current.drugId === drugId) return;
    previousValues.current.drugId = drugId;
    setValue("dosage_form", formOptions[0] ?? "");
  }, [drugId, formOptions, setValue]);

  useEffect(() => {
    if (previousValues.current.dosageForm === dosageForm) return;
    previousValues.current.dosageForm = dosageForm;
    setValue("dosage", dosages[0] ?? "");
  }, [dosageForm, dosages, setValue]);

  useEffect(() => {
    if (previousValues.current.dosage === dosage && previousValues.current.useCustomQuantity === useCustomQuantity) return;
    previousValues.current.dosage = dosage;
    previousValues.current.useCustomQuantity = useCustomQuantity;
    setValue("quantity", useCustomQuantity ? "" : quantities[0] ?? "");
  }, [dosage, useCustomQuantity, quantities, setValue]);

  useEffect(() => {
    if (previousValues.current.useCustomDaysSupply === useCustomDaysSupply) return;
    previousValues.current.useCustomDaysSupply = useCustomDaysSupply;
    setValue("days_supply", useCustomDaysSupply ? "" : DAYS_SUPPLY_OPTIONS[0]);
  }, [useCustomDaysSupply, setValue]);

  // the current location checkbox is only offered when the browser can provide a position
  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(
      (position) => setCurrentLocation({
        latitude: String(position.coords.latitude),
        longitude: String(position.coords.longitude),
      }),
      () => {
        setCurrentLocation(null);
        setValue("use_current_location", false);
      },
    );
  }, [setValue]);

  const handleSubmit = useCallback(async (values: CheckCoverageFormData) => {
    setError(undefined);

    let location = values.use_current_location ? currentLocation : null;
    if (!location) {
      try {
        const geolocation = await findBestPriceGeolocateAPI(geolocateRequest(token, values.location), getAuthToken());
        if (geolocation.latitude && geolocation.longitude) {
          location = { latitude: geolocation.latitude, longitude: geolocation.longitude };
        }
      } catch (err) {
        console.error("Failed to geolocate check coverage location:", err);
      }
    }

    if (!location) {
      setError("We couldn't find that location. Please enter a valid address or ZIP code.");
      return;
    }

    onSearch(
      {
        drug: selectedDrug,
        dosage_form: values.dosage_form,
        dosage: values.dosage,
        quantity: Number(values.quantity),
        days_supply: Number(values.days_supply),
        location,
      },
      values,
    );
  }, [currentLocation, token, getAuthToken, selectedDrug, onSearch]);

  return (
    <Stack spacing="medium-large" className={styles.FormContainer}>
      <Title>{drugResponse.drug.drug_name}</Title>

      {error && (
        <Message appearance={Messages.Error} visible>
          {error}
        </Message>
      )}

      <Form form={form} onSubmit={handleSubmit}>
        <Stack spacing="medium">
          <Dropdown.Field name="drug_id" label="Type" required testId="check_coverage_type">
            {drugOptions.map((drug) => (
              <Dropdown.Option key={drug.id} value={String(drug.id)}>
                {`${drug.drug_name} (${drug.drug_type})`}
              </Dropdown.Option>
            ))}
          </Dropdown.Field>

          <Dropdown.Field name="dosage_form" label="Form" required testId="check_coverage_form">
            {formOptions.map((option) => (
              <Dropdown.Option key={option} value={option}>{option}</Dropdown.Option>
            ))}
          </Dropdown.Field>

          <Dropdown.Field name="dosage" label="Dosage" required testId="check_coverage_dosage">
            {dosages.map((option) => (
              <Dropdown.Option key={option} value={option}>{option}</Dropdown.Option>
            ))}
          </Dropdown.Field>

          <Stack spacing="small">
            {useCustomQuantity ? (
              <Textbox.Field
                name="quantity"
                label="Quantity"
                required
                validator={validateCustomNumber}
                testId="check_coverage_custom_quantity"
              />
            ) : (
              <Dropdown.Field name="quantity" label="Quantity" placeholder="Select" required testId="check_coverage_quantity">
                {quantities.map((option) => (
                  <Dropdown.Option key={option} value={option}>{pluralizeQuantity(option, packaging)}</Dropdown.Option>
                ))}
              </Dropdown.Field>
            )}
            <Checkbox.Field
              name="use_custom_quantity"
              label="Enter Custom Quantity"
              hideLabel
              testId="check_coverage_use_custom_quantity"
            />
          </Stack>

          <Stack spacing="small">
            {useCustomDaysSupply ? (
              <Textbox.Field
                name="days_supply"
                label="Days Supply"
                required
                validator={validateCustomNumber}
                testId="check_coverage_custom_days_supply"
              />
            ) : (
              <Dropdown.Field name="days_supply" label="Days Supply" placeholder="Select" required testId="check_coverage_days_supply">
                {DAYS_SUPPLY_OPTIONS.map((option) => (
                  <Dropdown.Option key={option} value={option}>{option}</Dropdown.Option>
                ))}
              </Dropdown.Field>
            )}
            <Checkbox.Field
              name="use_custom_days_supply"
              label="Enter Custom Days Supply"
              hideLabel
              testId="check_coverage_use_custom_days_supply"
            />
          </Stack>

          <Stack spacing="small">
            <Textbox.Field
              name="location"
              label="Location"
              required={!useCurrentLocation}
              disabled={useCurrentLocation}
              testId="check_coverage_location"
            />
            {currentLocation && (
              <Checkbox.Field
                name="use_current_location"
                label="Use My Current Location"
                hideLabel
                testId="check_coverage_use_current_location"
              />
            )}
          </Stack>

          <div>
            <SubmitButton
              appearance={getThemeButton("primary", primary_color)}
              icon="arrow-right"
              iconPosition="right"
              testId="check_coverage_submit"
            >
              Check Patient Coverage
            </SubmitButton>
          </div>
        </Stack>
      </Form>
    </Stack>
  );
};
